import React from 'react';
import { Users, Crown } from 'lucide-react';
import MetricGroup, { MetricField } from '../MetricGroup';
import type { ServerStats, SubscriberMetrics } from '../../../types';
import { ThresholdLevel } from '../../../utils/thresholds';

interface Props {
  serverStats: ServerStats | null;
}

const DistributorSubs: React.FC<Props> = ({ serverStats }) => {
  const sv = serverStats;
  const dominant = sv?.dominant_engine || 'default';
  const ed = sv?.per_engine && dominant !== 'default'
    ? (sv.per_engine as any)[dominant]
    : null;

  const subs: SubscriberMetrics[] = ed?.subscribers || [];
  const activeCount = ed?.active_subscriber_count ?? sv?.active_subscriber_count ?? 0;
  const leader = subs.find((s) => s.is_leader);

  // outlier 检测：min_bitrate * 3 < max_bitrate
  let outlierLevel: ThresholdLevel = 'normal';
  if (subs.length > 1) {
    const bitrates = subs.map((s) => s.bitrate_kbps).filter((b) => b > 0);
    if (bitrates.length > 1) {
      const min = Math.min(...bitrates);
      const max = Math.max(...bitrates);
      if (min > 0 && min * 3 < max) outlierLevel = 'warn';
    }
  }

  const fields: MetricField[] = [
    { label: '活跃订阅者', value: activeCount, unit: '路' },
  ];

  if (leader) {
    fields.push({ label: 'Leader', value: `id ${leader.id}`,
      tooltip: '获得输入控制权的 subscriber（仅软编有 leader 概念）' });
  }

  if (subs.length > 1 && outlierLevel === 'warn') {
    const bitrates = subs.map((s) => s.bitrate_kbps);
    fields.push({
      label: 'BWE 离群',
      value: 'WARN',
      level: 'warn',
      tooltip: `min=${Math.min(...bitrates)} max=${Math.max(...bitrates)}（min×3 < max）`,
    });
  }

  // per-subscriber 列表 footer
  const footer = subs.length > 0 ? (
    <div className="space-y-1">
      <div className="text-[9px] text-zinc-500 uppercase tracking-wide">订阅者明细</div>
      {subs.map((s) => (
        <div key={s.id} className="flex justify-between items-center text-[10px] py-0.5">
          <span className="flex items-center gap-1 text-zinc-400">
            {s.is_leader && <Crown size={10} className="text-yellow-500" />}
            <span className="font-mono">id {s.id}</span>
          </span>
          <span className="font-mono text-zinc-300">
            {s.bitrate_kbps} kbps · {s.framerate_fps} fps
            {s.last_update_age_ms >= 0 && (
              <span className="ml-1 opacity-50">({s.last_update_age_ms}ms 前)</span>
            )}
          </span>
        </div>
      ))}
    </div>
  ) : (
    <div className="text-[10px] text-zinc-500 text-center py-2">无活跃订阅者</div>
  );

  return (
    <MetricGroup
      title="分发 / 订阅"
      icon={<Users size={14} />}
      level={outlierLevel}
      primary={{
        label: '订阅者数',
        value: activeCount,
        unit: '路',
        level: outlierLevel,
      }}
      fields={fields}
      footer={footer}
    />
  );
};

export default DistributorSubs;
